import { useMutation, useQuery } from '@tanstack/react-query';
import { attachPatientNote } from 'api/api';
import Graphic from 'components/Graphic/Graphic';
import Icon, { IconSprite } from 'components/Icon/Icon';
import Image from 'components/Image/Image';
import NotesForm from 'components/Modal/NotesForm/NotesForm';
import moment from 'moment';
import { notesQuery } from 'pages/PatientRecords/PatientRecords';
import { isEmpty } from 'ramda';
import React from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { AuthUser, HTTPError, Note, Patient } from 'types/types';
import { randomTempIdGenerator, truncateWithEllipses } from 'utils/utils';

function NoteLists({
  user,
  patient,
  onSyncAddedNote,
  onShowAlert,
}: {
  user: AuthUser;
  patient: Patient;
  onSyncAddedNote: (note: Note) => void;
  onShowAlert: (error: HTTPError) => void;
}) {
  const [tab, setTab] = React.useState<'ALL' | 'PRIVATE' | 'PRIVATE_ADMIN'>('ALL');
  const [showNotesModal, setShowNotesModal] = React.useState<
    undefined | 'ALL' | 'PRIVATE' | 'PRIVATE_ADMIN'
  >(undefined);
  const [noteLimitCount, setNoteLimitCount] = React.useState(10);
  const [viewNote, setViewNote] = React.useState<undefined | Note>(undefined);

  const { data, isLoading } = useQuery({
    ...notesQuery(patient, tab),
    enabled: !!patient,
  });

  const notes = (data || []) as Note[];

  const { mutate: addPatientNoteFn, isLoading: isAddingNote } = useMutation(
    (variables: { patientId: string; payload: Record<string, any>; attachmentBlob?: string }) =>
      attachPatientNote(variables.patientId, variables.payload),
    {
      onSuccess: (_, variables) => {
        const newNote = {
          id: randomTempIdGenerator(),
          ...variables.payload.extendedNotes[0],
          ...(variables.attachmentBlob && { attachmentBlob: variables.attachmentBlob }),
          author: {
            ...variables.payload.extendedNotes[0].author,
            signedAvatar: user?.signedAvatar,
          },
        };

        onSyncAddedNote(newNote);

        setShowNotesModal(undefined);
      },
      onError: (error: HTTPError) => {
        onShowAlert(error);
      },
    },
  );

  const handleAddedNote = (newNote: Partial<Note>) => {
    const author = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      profilePicture: user.profilePicture,
    };

    const payload = { ...newNote, authorId: author.id };

    delete payload.attachmentBlob;

    addPatientNoteFn({
      patientId: patient.id,
      attachmentBlob: newNote.attachmentBlob,
      payload: {
        extendedNotes: [{ ...payload, author }],
        notes: patient?.patientDetails?.notes || 'None',
      },
    });
  };

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex items-center space-x-7 border-b border-[#e9ecef]/90">
          <button
            onClick={() => {
              setTab('ALL');
            }}
            type="button"
            className={`text-[15px] ${
              tab === 'ALL'
                ? 'border-b-2 border-river-black !text-river-black font-medium'
                : 'text-zinc-400'
            }`}
          >
            All chart notes
          </button>
          <button
            onClick={() => {
              setTab('PRIVATE');
            }}
            type="button"
            className={`text-[15px] ${
              tab === 'PRIVATE'
                ? 'border-b-2 border-river-black !text-river-black font-medium'
                : 'text-zinc-400'
            }`}
          >
            Private chart notes
          </button>
          <button
            onClick={() => {
              setTab('PRIVATE_ADMIN');
            }}
            type="button"
            className={`text-[15px] ${
              tab === 'PRIVATE_ADMIN'
                ? 'border-b-2 border-river-black !text-river-black font-medium'
                : 'text-zinc-400'
            }`}
          >
            Private admin notes
          </button>
        </div>

        {(tab === 'PRIVATE' || tab === 'ALL') && (
          <div className="flex items-center space-x-5 py-6 border-b border-[#e9ecef]">
            <button
              onClick={() => setShowNotesModal('ALL')}
              type="button"
              className="text-river-blue inline-flex items-center text-[15px] whitespace-pre"
            >
              <Icon id="addPlusSign" className="w-[12px] h-[12px] text-river-blue mr-2" />
              Add <span className="hidden tablet:inline-flex">chart </span>note
            </button>
            <button
              onClick={() => setShowNotesModal('PRIVATE')}
              type="button"
              className="text-river-blue inline-flex items-center text-[15px] whitespace-pre"
            >
              <Icon id="addPlusSign" className="w-[12px] h-[12px] text-river-blue mr-2" />
              Add private <span className="hidden tablet:inline-flex">chart </span>note
            </button>
          </div>
        )}

        <div>
          {isLoading ? (
            <div
              className="mx-auto text-center mt-4 mb-4 tablet:mt-10 tablet:mb-10 w-28 
              flex justify-center items-start flex-col"
            >
              <Icon id="loader" className="w-10 h-10 text-river-black mx-auto" />
              <span className="loading">Please wait</span>
            </div>
          ) : (
            <>
              {isEmpty(notes) ? (
                <div className="w-full flex flex-col justify-center items-center pt-8 pb-6">
                  <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
                  <div className="text-center mt-3">
                    <p className="font-medium">No patient notes found.</p>
                    <p className="text-neutral-500 text-xs">
                      All chart notes will be displayed here
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-[#e9ecef]">
                  {notes.slice(0, noteLimitCount).map((fndNote, index) => (
                    <div
                      role="presentation"
                      className="py-3"
                      key={`${fndNote.date}_${index}`}
                      onClick={() => (!(isMobile && !isTablet) ? false : setViewNote(fndNote))}
                    >
                      <div
                        className="cursor-pointer tablet:cursor-default flex w-full justify-between space-x-4 py-1 
                       hover:bg-[#F6FAFF] hover:rounded-full"
                      >
                        <div className="flex items-center space-x-3 w-full tablet:w-[20%]">
                          <Image
                            className="h-[50px] w-[50px] tablet:h-12 tablet:w-12 rounded-full shrink-0"
                            src={
                              fndNote.author?.signedAvatar || 'image_river_avatar_placeholder.svg'
                            }
                            alt="author avatar"
                            bgColor=""
                          />
                          <div className="flex flex-row flex-wrap w-full">
                            <div className="text-river-black font-medium text-sm">{`${fndNote.author?.firstName} ${fndNote.author?.lastName}`}</div>
                            <div className="flex items-center text-[#737373] font-normal text-xs ml-auto laptop:hidden">
                              {moment(fndNote.date, 'YYYY-MM-DDTHH:mm:ssZ').format('MM/DD/YYYY')}
                            </div>
                            <div className="flex w-full items-center text-[#737373] font-normal text-xs laptop:hidden">
                              {truncateWithEllipses(fndNote.text, 60)}
                            </div>
                          </div>
                        </div>
                        <div className="hidden laptop:flex tablet:w-[10%] items-center text-[#737373] font-normal text-sm">
                          {fndNote.date
                            ? moment(fndNote.date, 'YYYY-MM-DDTHH:mm:ssZ').format('MM/DD/YYYY')
                            : '-'}
                        </div>
                        <div className="hidden laptop:flex flex-1 items-center text-[#737373] text-sm">
                          {truncateWithEllipses(fndNote.text, 110)}
                        </div>
                        <div className="hidden laptop:flex tablet:w-[10%] items-center">
                          <span
                            className={`flex rounded-full w-[37px] h-[37px] justify-center items-center bg-[#E2F4FD] ${
                              isEmpty(fndNote.attachmentBlob) || isEmpty(fndNote.attachment)
                                ? 'opacity-30'
                                : ''
                            }`}
                          >
                            <IconSprite
                              id="icon-paperclip"
                              className="w-[20px] h-[20px] text-river-black stroke-river-black"
                            />
                          </span>
                        </div>
                        <div className="hidden laptop:flex tablet:w-[10%] items-center text-sm">
                          <button
                            onClick={() => setViewNote(fndNote)}
                            type="button"
                            className="bg-gray-100 text-xs rounded-full py-[2px] px-5 inline-flex items-center h-[35px] flex-shrink-0"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {notes.length > 10 && (
                <div className="text-center mt-3 mb-0 tablet:mt-2 text-river-blue">
                  <button
                    onClick={() => setNoteLimitCount(noteLimitCount === 10 ? notes.length : 10)}
                    type="button"
                    className="p-2 !font-medium"
                  >
                    {noteLimitCount === 10 ? 'Show all notes...' : 'Show less notes...'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {(Boolean(showNotesModal) || Boolean(viewNote)) && (
        <NotesForm
          note={viewNote}
          onCloseModal={() => {
            setShowNotesModal(undefined);
            setViewNote(undefined);
          }}
          isAddingNote={isAddingNote}
          isOwner={Boolean(viewNote) && viewNote?.author.id === user.id}
          isPrivateNote={showNotesModal === 'PRIVATE'}
          onAddNote={handleAddedNote}
        />
      )}
    </>
  );
}

export default NoteLists;
